import type { Delta } from "../types/deltas";
import type { UserState } from "../types/state";

import { PathHelpers } from "./PathHelpers";

/**
 * Minimal logger contract used by {@link DeltaHelpers}. This is intentionally
 * narrow so the shared delta logic stays browser-safe: callers may inject the
 * Bridge (Splunk/Sentry) logger on the server, while the browser falls back to
 * `console`. Keeping this module free of `../logger` prevents the UI bundle from
 * pulling in server-only Node/@sentry/node code.
 */
export interface DeltaLogger {
  error: (message: string, meta?: unknown) => void;
}

const defaultLogger: DeltaLogger = {
  error: (message: string, meta?: unknown): void => {
    console.error(message, meta);
  },
};

export class DeltaHelpers {
  /**
   * The null tombstone every outstanding_requests clear writes, whether from
   * the reducer's linked-request removal, the stale sweep, or a one-off
   * side-effect clear. Centralized so all three stay in sync if the tombstone
   * shape ever changes.
   */
  public static buildClearOutstandingRequestDelta = (
    requestId: string,
  ): Delta => ({
    path: `user_state.outstanding_requests.${requestId}`,
    op: "value_set",
    value: null,
  });

  public static applyDeltaInPlace = (
    state: UserState,
    delta: Delta,
    logger: DeltaLogger = defaultLogger,
  ): boolean => {
    // ADO-1467: a late stream/tool delta can target a chat that was pruned on
    // disconnect mid-turn. Never write through a non-array message list.
    // Runs in the shared delta path, so it guards all clients.
    const chatWrite = PathHelpers.parseChatWrite(delta.path);
    if (chatWrite) {
      const { chatId, intoMessageList, messageIndex, nestedUnderMessage } =
        chatWrite;
      // Sanitized metadata only — never log `delta` itself, which carries
      // delta.value (chat / generated content).
      const meta = { path: delta.path, op: delta.op, chatId };
      const chat = state.chats?.[chatId];
      if (!chat) {
        logger.error("Could not apply delta: target chat is not loaded", meta);
        return false;
      }
      if (intoMessageList && !Array.isArray(chat.messages)) {
        logger.error("Could not apply delta: chat.messages is not a list", {
          ...meta,
          messagesType: typeof chat.messages,
        });
        return false;
      }
      // ADO-1467 (null-padding): Defensive guard against null-padding.
      if (messageIndex !== null && delta.op === "value_set") {
        const currentLength = chat.messages.length;
        const maxIndex = nestedUnderMessage ? currentLength - 1 : currentLength;
        if (messageIndex > maxIndex) {
          logger.error(
            "Could not apply delta: message index would create a sparse array",
            { ...meta, messageIndex, currentLength },
          );
          return false;
        }
      }
    }

    const { parent, parentKey } = PathHelpers.followPath(
      state,
      delta.path,
      true, // createMissing
    );

    if (parentKey === null) {
      // Handle root-level replacement when path is "user_state"
      if (delta.path === "user_state" && delta.op === "value_set") {
        // Clear existing state and copy new properties
        for (const key of Object.keys(state)) {
          delete (state as Record<string, unknown>)[key];
        }
        Object.assign(state, delta.value);
        return true;
      }
      logger.error("Could not apply delta: parentKey = null", { delta });
      return false;
    }

    switch (delta.op) {
      case "value_set":
        parent[parentKey] = delta.value;
        return true;
      case "text_append":
        if (typeof parent[parentKey] !== "string") {
          logger.error(
            "Could not apply delta: existing value is not a string",
            { delta },
          );
          return false;
        }
        parent[parentKey] += delta.value;
        return true;
      case "list_append":
        if (parent[parentKey] == null) {
          parent[parentKey] = [];
        }
        if (!Array.isArray(parent[parentKey])) {
          logger.error("Could not apply delta: existing value is not a list", {
            delta,
          });
          return false;
        }
        parent[parentKey].push(delta.value);
        return true;
      case "list_extend":
        if (parent[parentKey] == null) {
          parent[parentKey] = [];
        }
        if (!Array.isArray(parent[parentKey])) {
          logger.error("Could not apply delta: existing value is not a list", {
            delta,
          });
          return false;
        }
        parent[parentKey] = [...parent[parentKey], ...delta.value];
        return true;
      default:
        logger.error("Could not apply delta: unknown operation", { delta });
        return false;
    }
  };
}
